import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';
import { IAlarmConfiguration } from '../types/ISettings';

import {
  MESSAGES,
  SCOPES,
  createNotificationHandler,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';

export const alarmApi = createApi({
  reducerPath: 'alarmApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['AlarmConfigurations'],
  endpoints: (builder) => ({
    getAlarmConfigurations: builder.query<IAlarmConfiguration[], void>({
      query: () => ({
        url: 'Configurations/Alarms',
        method: 'get',
      }),
      providesTags: ['AlarmConfigurations'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_ALARM,
        MESSAGES.LOAD,
        false
      ),
    }),
    createAlarmConfiguration: builder.mutation<
      IAlarmConfiguration,
      Partial<IAlarmConfiguration>
    >({
      query: (configuration) => ({
        url: 'Configurations/Alarms',
        method: 'post',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_ALARM,
          MESSAGES.ADD
        )(configuration, { dispatch, queryFulfilled });

        const { data: newItem } = await queryFulfilled;
        dispatch(
          alarmApi.util.updateQueryData(
            'getAlarmConfigurations',
            undefined,
            (draft) => {
              draft.push(newItem);
            }
          )
        );
      },
    }),
    updateAlarmConfiguration: builder.mutation<
      IAlarmConfiguration,
      IAlarmConfiguration
    >({
      query: (configuration) => ({
        url: `Configurations/Alarms/${configuration.id}`,
        method: 'put',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_ALARM,
          MESSAGES.UPDATE
        )(configuration, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;
        dispatch(
          alarmApi.util.updateQueryData(
            'getAlarmConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === updatedItem.id);
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], ...updatedItem };
              }
            }
          )
        );
      },
    }),
    removeAlarmConfiguration: builder.mutation<
      { success: boolean },
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `Configurations/Alarms/${id}`,
        method: 'delete',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_ALARM,
          MESSAGES.DELETE
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;
        dispatch(
          alarmApi.util.updateQueryData(
            'getAlarmConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetAlarmConfigurationsQuery,
  useCreateAlarmConfigurationMutation,
  useUpdateAlarmConfigurationMutation,
  useRemoveAlarmConfigurationMutation,
} = alarmApi;

export default alarmApi;
